import { maybe } from '@passionware/monads';
import { Capability } from '__types__';
import { Fragment } from 'react';
import {
  CircleDashed,
  Clapperboard,
  Copy,
  KeyRound,
  Palette,
  Mail,
  QrCode,
  Settings,
  Stamp,
  Type,
  Users
} from 'lucide-react';
import type { EnterpriseWorkspaceSettingsRoutes } from 'v5/core/routing/appRouting';
import { NavLink } from 'v5/platform/react-router';
import { MenuItem, MenuSeparator } from 'v5/patterns/sidebar/Menu';
import { ifCan } from 'v5/platform/react/ifCan';
import { AccessQuery } from 'v5/services/front/AccessService/AccessService';

export function renderEnterpriseWorkspaceSettingsItems(
  wsSettings: EnterpriseWorkspaceSettingsRoutes,
  level = 1,
  options: {
    can: (query: AccessQuery) => boolean;
    resourceTypesLabel?: string;
  }
) {
  const { can } = options;
  const resourceTypesLabel = options.resourceTypesLabel ?? 'Resource types';

  return ifCan(can)
    .render(can => {
      const general = ifCan(can)
        .render(can => (
          <>
            {can({ action: Capability.CONFIGURE_WORKSPACE }) && (
              <NavLink to={wsSettings.general()}>
                <MenuItem level={level} icon={<Settings className="size-4" />}>
                  General
                </MenuItem>
              </NavLink>
            )}
            {can({ action: Capability.CONFIGURE_WORKSPACE }) && (
              <NavLink to={wsSettings.theming()}>
                <MenuItem level={level} icon={<Palette className="size-4" />}>
                  Theming
                </MenuItem>
              </NavLink>
            )}
            {can({ action: Capability.CONFIGURE_TEAM }) && (
              <NavLink to={wsSettings.users()}>
                <MenuItem level={level} icon={<Users className="size-4" />}>
                  Users
                </MenuItem>
              </NavLink>
            )}
            {can({ action: Capability.CONFIGURE_TEAM }) && (
              <NavLink to={wsSettings.permissionGroups()}>
                <MenuItem level={level} icon={<KeyRound className="size-4" />}>
                  Permission groups
                </MenuItem>
              </NavLink>
            )}
          </>
        ))
        .otherwise(null);

      const resources = ifCan(can)
        .render(can => (
          <>
            {can({ action: Capability.CONFIGURE_RESOURCE_TYPES }) && (
              <NavLink to={wsSettings.resourceTypes()}>
                <MenuItem level={level} icon={<Type className="size-4" />}>
                  {resourceTypesLabel}
                </MenuItem>
              </NavLink>
            )}
            {can({ action: Capability.MANAGE_RESOURCE_DUPLICATES }) && (
              <NavLink to={wsSettings.resourceDuplicates()}>
                <MenuItem level={level} icon={<Copy className="size-4" />}>
                  Resource duplicates
                </MenuItem>
              </NavLink>
            )}
          </>
        ))
        .otherwise(null);

      const bookings = ifCan(can)
        .render(can => (
          <>
            {can({ action: Capability.CONFIGURE_BOOKING_TYPES }) && (
              <NavLink to={wsSettings.bookingTypes()}>
                <MenuItem level={level} icon={<Stamp className="size-4" />}>
                  Booking types
                </MenuItem>
              </NavLink>
            )}
            {can({ action: Capability.CONFIGURE_STATUSES }) && (
              <NavLink to={wsSettings.bookingWorkflow()}>
                <MenuItem
                  level={level}
                  icon={<CircleDashed className="size-4" />}
                >
                  Booking workflow
                </MenuItem>
              </NavLink>
            )}
            {can({
              action: Capability.CONFIGURE_BOOKING_CONFIRMATION_TEMPLATES
            }) && (
              <NavLink to={wsSettings.confirmationTemplates()}>
                <MenuItem level={level} icon={<Mail className="size-4" />}>
                  Confirmation templates
                </MenuItem>
              </NavLink>
            )}
          </>
        ))
        .otherwise(null);

      const productions = ifCan(can)
        .render(can => (
          <>
            {can({ action: Capability.CONFIGURE_PRODUCTION_TYPES }) && (
              <NavLink to={wsSettings.productionTypes()}>
                <MenuItem
                  level={level}
                  icon={<Clapperboard className="size-4" />}
                >
                  Production types
                </MenuItem>
              </NavLink>
            )}
            {can({ action: Capability.CONFIGURE_STATUSES }) && (
              <NavLink to={wsSettings.productionWorkflow()}>
                <MenuItem
                  level={level}
                  icon={<CircleDashed className="size-4" />}
                >
                  Production workflow
                </MenuItem>
              </NavLink>
            )}
          </>
        ))
        .otherwise(null);

      const shortlists = ifCan(can)
        .render(can =>
          // Saving the mapping needs Configure workspace, so the page needs both.
          can({ action: Capability.CONFIGURE_CUSTOM_NIKE_QR_CODE }) &&
          can({ action: Capability.CONFIGURE_WORKSPACE }) ? (
            <NavLink to={wsSettings.qrCodeMapping()}>
              <MenuItem level={level} icon={<QrCode className="size-4" />}>
                Access QR code
              </MenuItem>
            </NavLink>
          ) : null
        )
        .otherwise(null);

      return (
        <>
          {[general, resources, bookings, productions, shortlists]
            .filter(maybe.isPresent)
            .map((section, index) => (
              <Fragment key={index}>
                {index > 0 && <MenuSeparator level={level} />}
                {section}
              </Fragment>
            ))}
        </>
      );
    })
    .otherwise(null);
}
